import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DialogFooter } from "@/components/ui/dialog";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetDescription,
} from "@/components/ui/sheet";
import { Plus, X, Barbell, BookOpen } from "@phosphor-icons/react";
import { BLOCK_TYPES } from "./blockMeta";
import { useAuth } from "@/contexts/AuthContext";
import { getUnit } from "@/lib/weight";
import WeightInput, { toCanonicalLb, fromCanonicalLb } from "@/components/app/WeightInput";
import { toCanonicalMeters, toCanonicalInches, fromCanonicalMeters } from "@/lib/units";
import { INTERVAL_GOALS, DEFAULT_INTERVAL_GOAL, toSeconds } from "@/lib/intervals";
import MinSecInput from "@/components/app/MinSecInput";
import IntervalsConfig from "./IntervalsConfig";

// One-tap presets for the most common ladder/scheme patterns in CrossFit.
// `value` is the same comma-separated format the manual input emits so the
// match-highlight logic stays simple.
const REP_SCHEME_PRESETS = [
  { slug: "21-15-9",    label: "21-15-9",      value: "21, 15, 9",                                hint: "Fran / Diane / Elizabeth" },
  { slug: "30-20-10",   label: "30-20-10",     value: "30, 20, 10",                               hint: "Higher-volume conditioning" },
  { slug: "10-8-6-4-2", label: "10-8-6-4-2",   value: "10, 8, 6, 4, 2",                           hint: "Strength descending ladder" },
  { slug: "asc-1-10",   label: "1 → 10 (asc)", value: "1, 2, 3, 4, 5, 6, 7, 8, 9, 10",            hint: "Ascending ladder (1 up to 10)" },
  { slug: "desc-10-1",  label: "10 → 1 (desc)", value: "10, 9, 8, 7, 6, 5, 4, 3, 2, 1",           hint: "Descending ladder (10 down to 1)" },
];

export default function BlockForm({ type, initial, onSave, onCancel, isEdit }) {
  const { user } = useAuth();
  const userUnit = getUnit(user);
  const [b, setB] = useState(initial || {
    title: "",
    notes: "",
    rounds: type === "metcon" ? 3 : type === "tabata" ? 8 : type === "emom" ? 10 : type === "circuit" ? 3 : type === "intervals" ? 5 : null,
    time_cap_minutes: type === "metcon" ? 10 : type === "amrap" ? 12 : null,
    interval_seconds: type === "emom" ? 60 : null,
    work_seconds: type === "tabata" ? 20 : null,
    rest_seconds: type === "tabata" ? 10 : null,
    // Tabata UI split: minutes + seconds fields, plus a separate rounds slot.
    tabata_work_min: type === "tabata" ? 0 : null,
    tabata_work_sec: type === "tabata" ? 20 : null,
    tabata_rest_min: type === "tabata" ? 0 : null,
    tabata_rest_sec: type === "tabata" ? 10 : null,
    rep_scheme: type === "ladder" ? "21, 15, 9" : "",
    emom_work_min: type === "emom" ? 1 : "",
    emom_work_sec: type === "emom" ? 0 : "",
    rest_min: type === "intervals" ? 1 : "",
    rest_sec: type === "intervals" ? 0 : "",
    interval_goal: DEFAULT_INTERVAL_GOAL,
    target_distance: type === "intervals" ? 500 : "",
    target_distance_unit: "m",
    target_calories: "",
    target_time_min: "",
    target_time_sec: "",
  });
  // Inline movements list — only when CREATING (not editing)
  const [movements, setMovements] = useState([]);
  const [draft, setDraft] = useState({
    name: "", weight: "", weight_unit: userUnit, reps: "", calories: "",
    distance: "", distance_unit: "m", height: "", height_unit: "in",
  });

  // Library picker (saved exercises + CrossFit movement library)
  const [libOpen, setLibOpen] = useState(false);
  const [libSource, setLibSource] = useState("crossfit");
  const [libQ, setLibQ] = useState("");
  const [savedLib, setSavedLib] = useState([]);
  const [cfLib, setCfLib] = useState([]);

  useEffect(() => {
    if (libOpen && savedLib.length === 0) {
      api.get("/exercises").then((r) => setSavedLib(r.data)).catch(() => {});
    }
    if (libOpen && cfLib.length === 0) {
      api.get("/movement-library").then((r) => setCfLib(r.data)).catch(() => {});
    }
  }, [libOpen, savedLib.length, cfLib.length]);

  const pickFromSaved = (ex) => {
    const exUnit = ex.weight_unit || userUnit;
    setDraft({
      name: ex.name,
      weight: fromCanonicalLb(ex.weight, exUnit) ?? "",
      weight_unit: exUnit,
      reps: ex.reps ?? "",
      calories: ex.calories ?? "",
      distance: fromCanonicalMeters(ex.distance_meters, ex.distance_unit || "m") ?? "",
      distance_unit: ex.distance_unit || "m",
      height: ex.height_inches != null ? ex.height_inches : "",
      height_unit: ex.height_unit || "in",
    });
    setLibOpen(false);
  };

  const pickFromCrossfit = (mv) => {
    setDraft({
      name: mv.name,
      weight: mv.default_weight != null ? fromCanonicalLb(mv.default_weight, userUnit) : "",
      weight_unit: userUnit,
      reps: mv.default_reps ?? "",
      calories: mv.default_calories ?? "",
      distance: fromCanonicalMeters(mv.default_distance_meters, "m") ?? "",
      distance_unit: "m",
      height: "",
      height_unit: "in",
    });
    setLibOpen(false);
  };

  const savedFiltered = savedLib.filter((e) => !libQ || e.name.toLowerCase().includes(libQ.toLowerCase()));
  const cfFiltered = cfLib.filter((m) => !libQ || m.name.toLowerCase().includes(libQ.toLowerCase()));
  const update = (k, v) => setB((p) => ({ ...p, [k]: v }));

  const showRepScheme = type === "metcon" || type === "amrap" || type === "ladder";
  const showInlineMovements = !isEdit;
  // In a Ladder, the block-level rep scheme drives every set's reps — so the
  // draft movement form only exposes Weight + Height. Reps/calories/distance
  // collapse out so coaches don't accidentally hardcode a per-movement rep.
  const schemeDrivesReps = type === "ladder";
  const schemeArr = (b.rep_scheme || "")
    .split(",").map((s) => Number(s.trim())).filter((n) => !isNaN(n));
  const schemeReplaceLabel = schemeArr.length ? `${schemeArr.join("-")} reps` : null;

  const addDraftMovement = () => {
    if (!draft.name.trim()) return;
    setMovements((m) => [...m, { ...draft, id: Math.random().toString(36).slice(2) }]);
    setDraft({
      name: "", weight: "", weight_unit: userUnit, reps: "", calories: "",
      distance: "", distance_unit: "m", height: "", height_unit: "in",
    });
  };
  const removeDraft = (id) => setMovements((m) => m.filter((x) => x.id !== id));

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const scheme = {};
        if (b.rounds != null && b.rounds !== "") scheme.rounds = Number(b.rounds);
        if (b.time_cap_minutes != null && b.time_cap_minutes !== "") {
          scheme.time_cap_seconds = Math.round(Number(b.time_cap_minutes) * 60);
        }
        if (b.interval_minutes != null && b.interval_minutes !== "") {
          scheme.interval_seconds = Math.round(Number(b.interval_minutes) * 60);
        }
        if (b.work_minutes != null && b.work_minutes !== "") {
          scheme.work_seconds = Math.round(Number(b.work_minutes) * 60);
        }
        if (b.rest_minutes != null && b.rest_minutes !== "") {
          scheme.rest_seconds = Math.round(Number(b.rest_minutes) * 60);
        }
        // Tabata: work_min + work_sec  / rest_min + rest_sec  (override the
        // minutes-only fields above when provided).
        if (type === "tabata") {
          const wm = Number(b.tabata_work_min || 0);
          const ws = Number(b.tabata_work_sec || 0);
          const rm = Number(b.tabata_rest_min || 0);
          const rs = Number(b.tabata_rest_sec || 0);
          const wTotal = wm * 60 + ws;
          const rTotal = rm * 60 + rs;
          if (wTotal > 0) scheme.work_seconds = wTotal;
          if (rTotal > 0) scheme.rest_seconds = rTotal;
        }
        if (type === "emom") {
          const w = toSeconds(b.emom_work_min, b.emom_work_sec);
          if (w > 0) scheme.interval_seconds = w;
          scheme.rest_seconds = toSeconds(b.rest_min, b.rest_sec);
        }
        if (type === "intervals") {
          const goal = INTERVAL_GOALS[b.interval_goal] || INTERVAL_GOALS[DEFAULT_INTERVAL_GOAL];
          scheme.interval_goal = b.interval_goal || DEFAULT_INTERVAL_GOAL;
          scheme.rest_seconds = toSeconds(b.rest_min, b.rest_sec);
          scheme.target_time_seconds = toSeconds(b.target_time_min, b.target_time_sec);
          if (goal.target === "calories" && b.target_calories !== "") scheme.target_calories = Number(b.target_calories);
          if (goal.target === "distance" && b.target_distance !== "") {
            scheme.target_distance_meters = toCanonicalMeters(b.target_distance, b.target_distance_unit);
            scheme.target_distance_unit = b.target_distance_unit || "m";
          }
        }
        if (showRepScheme && b.rep_scheme) {
          const arr = b.rep_scheme.split(",").map((s) => Number(s.trim())).filter((n) => !isNaN(n));
          if (arr.length) scheme.rep_scheme = arr;
        }
        const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
        const cleanedMovements = movements.map(({ id, ...rest }) => ({
          name: rest.name.trim(),
          sets: 1,
          reps: num(rest.reps),
          weight: toCanonicalLb(rest.weight, rest.weight_unit),
          weight_unit: rest.weight_unit || userUnit,
          calories: num(rest.calories),
          distance_meters: toCanonicalMeters(rest.distance, rest.distance_unit),
          distance_unit: rest.distance_unit || "m",
          height_inches: toCanonicalInches(rest.height, rest.height_unit),
          height_unit: rest.height_unit || "in",
        }));
        onSave({
          type,
          title: (b.title || "").trim() || null,
          notes: b.notes || null,
          scheme_config: scheme,
        }, cleanedMovements);
      }}
      className="space-y-4"
      data-testid="block-form"
    >
      <div>
        <Label className="hs-label">Title (optional)</Label>
        <Input
          value={b.title ?? ""}
          onChange={(e) => update("title", e.target.value)}
          className="mt-2 h-11 rounded-xl"
          placeholder={type === "metcon" ? "e.g., Fran" : type === "amrap" ? "e.g., Cindy" : "Name this section"}
          data-testid="block-title-input"
        />
      </div>

      {(type === "metcon" || type === "circuit" || type === "tabata" || type === "emom") && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="hs-label">{type === "emom" ? "Minutes" : "Rounds"}</Label>
            <Input type="number" min="1" value={b.rounds ?? ""} onChange={(e) => update("rounds", e.target.value)} className="mt-2 h-11 rounded-xl font-data" data-testid="block-rounds" />
          </div>
          {(type === "metcon") && (
            <div>
              <Label className="hs-label">Time cap (min)</Label>
              <Input type="number" min="1" step="0.5" value={b.time_cap_minutes ?? ""} onChange={(e) => update("time_cap_minutes", e.target.value)} className="mt-2 h-11 rounded-xl font-data" data-testid="block-time-cap" />
            </div>
          )}
        </div>
      )}
      {type === "amrap" && (
        <div>
          <Label className="hs-label">Time cap (min)</Label>
          <Input type="number" min="1" step="0.5" value={b.time_cap_minutes ?? ""} onChange={(e) => update("time_cap_minutes", e.target.value)} className="mt-2 h-11 rounded-xl font-data" data-testid="block-time-cap" />
        </div>
      )}
      {type === "emom" && (
        <div className="space-y-3" data-testid="emom-config">
          <MinSecInput label="Work interval" min={b.emom_work_min} sec={b.emom_work_sec}
            onChange={(m, s) => setB((p) => ({ ...p, emom_work_min: m, emom_work_sec: s }))}
            testId="emom-work" hint="1 min 0 sec = every minute" />
          <MinSecInput label="Rest between intervals (optional)" min={b.rest_min} sec={b.rest_sec}
            onChange={(m, s) => setB((p) => ({ ...p, rest_min: m, rest_sec: s }))}
            testId="emom-rest" hint="leave 0 for classic EMOM" />
          <p className="text-xs text-ink-3">e.g., <strong>0 min 45 sec</strong> work / <strong>0 min 15 sec</strong> rest.</p>
        </div>
      )}
      {type === "intervals" && <IntervalsConfig b={b} update={update} />}
      {type === "tabata" && (
        <div className="space-y-3" data-testid="tabata-config">
          <div>
            <Label className="hs-label">Work interval</Label>
            <div className="mt-2 grid grid-cols-2 gap-3">
              <Input type="number" min="0" step="1" value={b.tabata_work_min ?? ""} onChange={(e) => update("tabata_work_min", e.target.value)} className="h-11 rounded-xl font-data" placeholder="min" data-testid="tabata-work-min" />
              <Input type="number" min="0" max="59" step="1" value={b.tabata_work_sec ?? ""} onChange={(e) => update("tabata_work_sec", e.target.value)} className="h-11 rounded-xl font-data" placeholder="sec" data-testid="tabata-work-sec" />
            </div>
            <p className="text-[10px] text-ink-3 mt-1 font-data">
              = {(Number(b.tabata_work_min || 0) * 60 + Number(b.tabata_work_sec || 0)) || "—"}s
            </p>
          </div>
          <div>
            <Label className="hs-label">Rest interval</Label>
            <div className="mt-2 grid grid-cols-2 gap-3">
              <Input type="number" min="0" step="1" value={b.tabata_rest_min ?? ""} onChange={(e) => update("tabata_rest_min", e.target.value)} className="h-11 rounded-xl font-data" placeholder="min" data-testid="tabata-rest-min" />
              <Input type="number" min="0" max="59" step="1" value={b.tabata_rest_sec ?? ""} onChange={(e) => update("tabata_rest_sec", e.target.value)} className="h-11 rounded-xl font-data" placeholder="sec" data-testid="tabata-rest-sec" />
            </div>
            <p className="text-[10px] text-ink-3 mt-1 font-data">
              = {(Number(b.tabata_rest_min || 0) * 60 + Number(b.tabata_rest_sec || 0)) || "—"}s
            </p>
          </div>
          <p className="text-xs text-ink-3">Classic Tabata: <strong>0 min 20 sec work / 0 min 10 sec rest, 8 rounds</strong>. Set total rounds with the "Rounds" field above.</p>
        </div>
      )}
      {showRepScheme && (
        <div>
          <Label className="hs-label">Rep scheme (optional, comma separated)</Label>
          <div className="mt-2 flex gap-2 flex-wrap" data-testid="rep-scheme-presets">
            {REP_SCHEME_PRESETS.map((p) => (
              <button
                key={p.label}
                type="button"
                onClick={() => update("rep_scheme", p.value)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                  (b.rep_scheme || "").replace(/\s/g, "") === p.value.replace(/\s/g, "")
                    ? "bg-terracotta text-white border-terracotta"
                    : "bg-muted2 text-ink hover:bg-line border-line"
                }`}
                data-testid={`rep-scheme-preset-${p.slug}`}
                title={p.hint}
              >
                {p.label}
              </button>
            ))}
          </div>
          <Input
            value={b.rep_scheme ?? ""}
            onChange={(e) => update("rep_scheme", e.target.value)}
            className="mt-2 h-11 rounded-xl font-data"
            placeholder="21, 15, 9"
            data-testid="block-rep-scheme"
          />
          <p className="text-xs text-ink-3 mt-1">e.g., <strong>21-15-9</strong> applies to every movement in the block (Fran-style).</p>
        </div>
      )}

      {showInlineMovements && (
        <div className="border-t border-line pt-4">
          <Label className="hs-label flex items-center gap-1.5"><Barbell size={12} /> Movements in this section</Label>
          <p className="text-xs text-ink-3 mt-1">Add every movement that's part of this {BLOCK_TYPES[type]?.label}. They share the rep scheme above.</p>

          {movements.length > 0 && (
            <div className="mt-3 space-y-2">
              {movements.map((m) => (
                <div key={m.id} className="flex items-center gap-2 p-2.5 rounded-lg bg-muted2" data-testid={`draft-movement-${m.id}`}>
                  <Barbell size={14} className="text-moss shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{m.name}</div>
                    <div className="text-[11px] text-ink-3 font-data">
                      {[
                        m.weight && `@ ${m.weight} ${m.weight_unit || userUnit}`,
                        schemeDrivesReps && schemeReplaceLabel
                          ? schemeReplaceLabel
                          : m.reps && `${m.reps} reps`,
                        !schemeDrivesReps && m.distance && `${m.distance} ${m.distance_unit || "m"}`,
                        m.height && `↕${m.height} ${m.height_unit || "in"}`,
                        !schemeDrivesReps && m.calories && `${m.calories} cal`,
                      ].filter(Boolean).join(" · ") || "—"}
                    </div>
                  </div>
                  <Button type="button" size="icon" variant="ghost" onClick={() => removeDraft(m.id)} className="rounded-lg h-8 w-8 text-error hover:bg-red-50 hover:text-error">
                    <X size={14} />
                  </Button>
                </div>
              ))}
            </div>
          )}

          <div className="mt-3 grid grid-cols-2 gap-2">
            <div className="col-span-2 sm:col-span-1 flex items-center gap-2">
              <Input
                placeholder="Movement"
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                className="h-11 rounded-xl flex-1"
                data-testid="block-draft-name"
              />
              <Sheet open={libOpen} onOpenChange={setLibOpen}>
                <SheetTrigger asChild>
                  <Button
                    type="button"
                    variant="secondary"
                    className="h-11 rounded-xl px-3 shrink-0"
                    title="Pick from library"
                    data-testid="block-draft-pick-library"
                  >
                    <BookOpen size={16} />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
                  <SheetHeader className="text-left">
                    <SheetTitle className="font-heading">Pick a movement</SheetTitle>
                    <SheetDescription>Choose from your saved exercises or the full CrossFit movement library.</SheetDescription>
                  </SheetHeader>
                  <div className="mt-4 inline-flex bg-muted2 rounded-xl p-1 w-full">
                    <button
                      type="button"
                      onClick={() => setLibSource("crossfit")}
                      className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-semibold uppercase tracking-[0.12em] ${
                        libSource === "crossfit" ? "bg-surface text-moss" : "text-ink-3"
                      }`}
                      data-testid="block-lib-source-crossfit"
                    >
                      CrossFit · {cfLib.length || 0}
                    </button>
                    <button
                      type="button"
                      onClick={() => setLibSource("mine")}
                      className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-semibold uppercase tracking-[0.12em] ${
                        libSource === "mine" ? "bg-surface text-moss" : "text-ink-3"
                      }`}
                      data-testid="block-lib-source-mine"
                    >
                      My library
                    </button>
                  </div>
                  <Input
                    placeholder="Search…"
                    value={libQ}
                    onChange={(e) => setLibQ(e.target.value)}
                    className="mt-3 h-11 rounded-xl"
                    data-testid="block-lib-search"
                  />
                  {libSource === "crossfit" ? (
                    <div className="mt-4 space-y-2">
                      {cfFiltered.length === 0 && (
                        <div className="text-sm text-ink-3 text-center py-8">No CrossFit movements match.</div>
                      )}
                      {cfFiltered.map((mv) => (
                        <button
                          key={mv.name}
                          type="button"
                          onClick={() => pickFromCrossfit(mv)}
                          className="w-full text-left p-3 rounded-xl border border-line hover:bg-sage hover:border-sage transition-colors"
                          data-testid={`block-lib-cf-${mv.name.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 min-w-0">
                              <Barbell size={16} className="text-moss shrink-0" />
                              <span className="font-medium truncate">{mv.name}</span>
                            </div>
                            <span className="text-[10px] uppercase tracking-[0.15em] text-ink-3 shrink-0">{mv.category}</span>
                          </div>
                          <div className="mt-1 text-xs font-data text-ink-3">
                            {[mv.default_reps && `${mv.default_reps} reps`,
                              mv.default_weight && `@ ${mv.default_weight} lb`,
                              mv.default_calories && `${mv.default_calories} cal`,
                              mv.default_distance_meters && `${mv.default_distance_meters} m`,
                            ].filter(Boolean).join(" · ")}
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="mt-4 space-y-2">
                      {savedLib.length === 0 && (
                        <div className="text-sm text-ink-3 text-center py-8">
                          Your library is empty. Save exercises from the Library page first, or use the CrossFit tab above.
                        </div>
                      )}
                      {savedFiltered.map((ex) => (
                        <button
                          key={ex.id}
                          type="button"
                          onClick={() => pickFromSaved(ex)}
                          className="w-full text-left p-3 rounded-xl border border-line hover:bg-sage hover:border-sage transition-colors"
                          data-testid={`block-lib-saved-${ex.id}`}
                        >
                          <div className="flex items-center gap-2">
                            <Barbell size={16} className="text-moss" />
                            <span className="font-medium">{ex.name}</span>
                          </div>
                          <div className="mt-1 text-xs font-data text-ink-3">
                            {[ex.sets && `${ex.sets}×${ex.reps ?? "?"}`,
                              ex.weight && `@ ${ex.weight} ${ex.weight_unit || userUnit}`,
                              ex.calories && `${ex.calories} cal`,
                            ].filter(Boolean).join(" · ")}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </SheetContent>
              </Sheet>
            </div>
            <div className="col-span-2 sm:col-span-1">
              <div className="flex items-center gap-1 bg-muted2 rounded-xl pl-3 pr-1 h-11">
                <Input
                  placeholder="Weight"
                  type="number"
                  step={draft.weight_unit === "level" ? "1" : "0.5"}
                  min={draft.weight_unit === "level" ? "0" : undefined}
                  value={draft.weight}
                  onChange={(e) => setDraft({ ...draft, weight: e.target.value })}
                  className="h-9 rounded-lg font-data flex-1 border-0 bg-transparent shadow-none focus-visible:ring-0 px-0"
                  data-testid="block-draft-weight"
                />
                <div className="inline-flex bg-surface rounded-lg p-0.5">
                  {["lb", "kg", "level"].map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setDraft({
                        ...draft,
                        weight_unit: opt,
                        // Level and lb/kg are different measurement domains;
                        // clear the number when crossing that boundary.
                        weight: (opt === "level" || draft.weight_unit === "level") && opt !== draft.weight_unit
                          ? ""
                          : draft.weight,
                      })}
                      className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-[0.15em] ${
                        draft.weight_unit === opt ? "bg-moss text-white" : "text-ink-3"
                      }`}
                      data-testid={`block-draft-weight-unit-${opt}`}
                      title={opt === "level" ? "Machine level (bikes, stacks)" : opt}
                    >
                      {opt === "level" ? "L" : opt}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <Input
              placeholder="Reps"
              type="number"
              value={draft.reps}
              onChange={(e) => setDraft({ ...draft, reps: e.target.value })}
              className={`h-11 rounded-xl font-data ${schemeDrivesReps ? "hidden" : ""}`}
              data-testid="block-draft-reps"
            />
            <Input
              placeholder="Calories"
              type="number"
              value={draft.calories}
              onChange={(e) => setDraft({ ...draft, calories: e.target.value })}
              className={`h-11 rounded-xl font-data ${schemeDrivesReps ? "hidden" : ""}`}
              data-testid="block-draft-calories"
            />
            <div className={`col-span-2 sm:col-span-1 ${schemeDrivesReps ? "hidden" : ""}`}>
              <div className="flex items-center gap-1 bg-muted2 rounded-xl pl-3 pr-1 h-11">
                <Input
                  placeholder="Distance"
                  type="number"
                  step="0.1"
                  value={draft.distance}
                  onChange={(e) => setDraft({ ...draft, distance: e.target.value })}
                  className="h-9 rounded-lg font-data flex-1 border-0 bg-transparent shadow-none focus-visible:ring-0 px-0"
                  data-testid="block-draft-distance"
                />
                <div className="inline-flex bg-surface rounded-lg p-0.5">
                  {["m", "ft", "in"].map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setDraft({ ...draft, distance_unit: opt })}
                      className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-[0.15em] ${
                        draft.distance_unit === opt ? "bg-moss text-white" : "text-ink-3"
                      }`}
                      data-testid={`block-draft-distance-unit-${opt}`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="col-span-2 sm:col-span-1">
              <div className="flex items-center gap-1 bg-muted2 rounded-xl pl-3 pr-1 h-11">
                <Input
                  placeholder="Height"
                  type="number"
                  step="0.1"
                  value={draft.height}
                  onChange={(e) => setDraft({ ...draft, height: e.target.value })}
                  className="h-9 rounded-lg font-data flex-1 border-0 bg-transparent shadow-none focus-visible:ring-0 px-0"
                  data-testid="block-draft-height"
                />
                <div className="inline-flex bg-surface rounded-lg p-0.5">
                  {["in", "ft"].map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setDraft({ ...draft, height_unit: opt })}
                      className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-[0.15em] ${
                        draft.height_unit === opt ? "bg-moss text-white" : "text-ink-3"
                      }`}
                      data-testid={`block-draft-height-unit-${opt}`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <Button
            type="button"
            variant="secondary"
            onClick={addDraftMovement}
            disabled={!draft.name.trim()}
            className="mt-2 w-full rounded-xl h-10"
            data-testid="add-draft-movement"
          >
            <Plus size={14} className="mr-2" /> Add movement to section
          </Button>
          <p className="text-xs text-ink-3 mt-2">
            {schemeDrivesReps
              ? `Reps are driven by the block's scheme (${schemeReplaceLabel || "21-15-9"}) — just add the movement and any weight or height.`
              : "Tip: leave reps blank if the rep scheme above (e.g., 21-15-9) drives them."}
          </p>
        </div>
      )}

      <div>
        <Label className="hs-label">Notes</Label>
        <Textarea rows={2} value={b.notes ?? ""} onChange={(e) => update("notes", e.target.value)} className="mt-2 rounded-xl" placeholder="Strategy, scaling, partner format…" />
      </div>
      <DialogFooter>
        <Button type="button" variant="ghost" onClick={onCancel} className="rounded-xl">Cancel</Button>
        <Button type="submit" className="rounded-xl bg-moss hover:bg-moss-hover text-white hover:text-white" data-testid="save-block-button">Save section</Button>
      </DialogFooter>
    </form>
  );
}
